import React, {useReducer} from "react"
import {Checkbox, Spin} from "antd"
import {useTranslation} from "react-i18next"
import {cloneDeep, head, isEmpty} from "lodash"
import {BaseButton} from "components/buttons"
import {Icon} from "components/Icon"
import {checkHasPermissionToUpdateAdvisor, getFullNameWithEnrollmentId, handleError} from "helpers"
import {studentService, studentStaffContactsServiceV3} from "services"
import styles from "./AdvisorsPopup.module.css"
import {useModel} from "hooks"
import {useQuery} from "@tanstack/react-query"

type Staff = {
    staffProfileId: number
    firstName: string
    middleName: string
    lastName: string
    photo: string
    studentStaffContactId: number
    studentStaffContactRoleId: number
    studentProfileId: number
    isDefault: number
    add?: boolean
    remove?: boolean
    edit?: boolean
}

type State = {
    isSubmitting: boolean
    removedObsoleteAdvisors: any[]
}

type Props = {
    profileIds: number[]
    departmentId: number
    studentStaffContactRoleId?: number
    staffs: any[]
    changeStaffs: (staffs: any[]) => void
    getStaffs: () => Promise<void>
    onClose: (reload?: boolean) => void
    addMultiple?: boolean
    type?: string
    inSearchMode?: boolean
}

export function AdvisorBody(props: Props) {
    function reducer(state, action) {
        return {...state, ...action}
    }
    const initialState: State = {
        isSubmitting: false,
        removedObsoleteAdvisors: []
    }
    const [state, dispatch] = useReducer(reducer, initialState)
    const model = useModel()
    const {isSubmitting} = state
    const {
        type,
        addMultiple,
        profileIds,
        departmentId,
        staffs,
        changeStaffs,
        getStaffs,
        onClose,
        studentStaffContactRoleId,
        inSearchMode
    } = props
    const {t} = useTranslation(["common"])

    const {data: studentAdvisors, isLoading: isLoadingStudentAdvisors} = useQuery({
        queryKey: ["current-student-advisors", departmentId, profileIds],
        queryFn: async () => {
            const {data: studentAdvisors} = await studentService.getStudentAdvisors({
                filter: {
                    departmentId,
                    profileIds: profileIds
                }
            })
            let data: any = []
            if (!isEmpty(head(studentAdvisors))) {
                data = head(Object.values(head(studentAdvisors)))
            }
            return data
        },
        enabled: !!departmentId && !!profileIds.length && !inSearchMode
    })

    function getIsDefault(staff: Staff, index: number) {
        if (staff.isDefault) {
            return (
                <div className={styles.primaryLabelWrap}>
                    <label className={styles.defaultLabel}>Primary</label>
                </div>
            )
        }
        if (staff.studentStaffContactId && !staff.remove && !staff.isDefault && !addMultiple) {
            return (
                <Checkbox
                    checked={false}
                    onChange={() => {
                        makeDefault(index)
                    }}>
                    Make Primary
                </Checkbox>
            )
        }
        if (staff.add) {
            return (
                <Checkbox
                    checked={false}
                    onChange={() => {
                        makeDefault(index)
                    }}>
                    Make Primary
                </Checkbox>
            )
        }
    }

    function addAdvisor(index: number) {
        const copyStaffs = cloneDeep(staffs)
        copyStaffs[index].add = true
        copyStaffs[index].remove = false
        changeStaffs(copyStaffs)
    }

    async function addAdvisorQuery(staffsToAdd: Staff[]) {
        const payload: any[] = []
        let response: any
        try {
            const newStaffs: any[] = staffsToAdd.map((staff) => ({
                departmentId,
                contactProfileId: staff.staffProfileId,
                isDefault: staff.isDefault ? 1 : 0,
                studentStaffContactRoleId: studentStaffContactRoleId || undefined
            }))
            profileIds.forEach((profileId) => {
                newStaffs.forEach((staff) => payload.push({...staff, profileId}))
            })
            if (type === "reassign") {
                response = await studentStaffContactsServiceV3.reAssignAdvisor(payload)
            } else {
                response = await studentStaffContactsServiceV3.create(payload)
            }

            return response
        } catch (error) {
            handleError(error)
        }
    }

    function removeAdvisor(index: number) {
        const copyStaffs = cloneDeep(staffs)
        copyStaffs[index].add = false
        copyStaffs[index].remove = true
        copyStaffs[index].isDefault = false
        changeStaffs(copyStaffs)
    }

    function toggleObsoleteAdvisor(advisor: any) {
        const {removedObsoleteAdvisors} = state
        if (removedObsoleteAdvisors.some((staff) => staff.studentStaffContactId === advisor.studentStaffContactId)) {
            dispatch({
                removedObsoleteAdvisors: removedObsoleteAdvisors.filter(
                    (staff) => staff.studentStaffContactId !== advisor.studentStaffContactId
                )
            })
        } else {
            dispatch({removedObsoleteAdvisors: [...removedObsoleteAdvisors, advisor]})
        }
    }

    async function removeAdvisorQuery(studentStaffContactIds: number[]) {
        try {
            const response = await studentStaffContactsServiceV3.delete(studentStaffContactIds)
            return response
        } catch (error) {
            handleError(error)
        }
    }

    async function editAdvisorQuery(staffsToEdit: Staff[]) {
        try {
            const editStaffContacts = staffsToEdit.map(({studentStaffContactId, isDefault}) => ({
                studentStaffContactId,
                isDefault
            }))
            const response = await studentStaffContactsServiceV3.updateBulk(editStaffContacts)
            return response
        } catch (error) {
            handleError(error)
        }
    }

    async function updateStaffs() {
        const addAdvisors = staffs.filter((staff) => staff.add && !staff.studentStaffContactId)
        const removeAdvisors = staffs.filter((staff) => staff.remove && staff.studentStaffContactId)
        const editAdvisors = staffs.filter((staff) => staff.edit && staff.studentStaffContactId)
        const updatedStaffs = []
        if (addAdvisors.length) {
            updatedStaffs.push(addAdvisorQuery(addAdvisors))
        }
        if (removeAdvisors.length) {
            const studentStaffContactIds = removeAdvisors.map((staff) => staff.studentStaffContactId)
            updatedStaffs.push(removeAdvisorQuery(studentStaffContactIds))
        }
        if (editAdvisors.length) {
            updatedStaffs.push(editAdvisorQuery(editAdvisors))
        }
        if (state.removedObsoleteAdvisors.length) {
            const studentStaffContactIds = state.removedObsoleteAdvisors.map((staff) => staff.studentStaffContactId)
            updatedStaffs.push(removeAdvisorQuery(studentStaffContactIds))
        }
        if (!updatedStaffs.length) {
            onClose()
            return
        }
        try {
            dispatch({isSubmitting: true})
            await Promise.all(updatedStaffs)
            await getStaffs()
            onClose(true)
        } catch (error) {
            handleError(error)
        } finally {
            dispatch({isSubmitting: false})
        }
    }

    function makeDefault(index: number) {
        const copyStaffs: Staff[] = cloneDeep(staffs)
        copyStaffs.forEach((staff) => {
            if (staff.isDefault) {
                staff.isDefault = 0
                staff.edit = true
            }
        })
        copyStaffs[index].isDefault = 1
        copyStaffs[index].edit = true
        changeStaffs(copyStaffs)
    }

    const renderAdvisorInfos = () => {
        return (
            <div className={styles.tableWrapper}>
                <table className={styles.usersTable}>
                    <thead className={styles.titleColor}>
                        <tr>
                            <th>{t("advisorsPopup.staff")}</th>
                            <th className={styles.columnSize}>{t("advisorsPopup.title")}</th>
                            <th className={styles.columnSize}>{t("advisorsPopup.default")}</th>
                            <th>{t("advisorsPopup.role")}</th>
                            <th>{t("advisorsPopup.select")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {staffs.map((staff, index) => (
                            <tr
                                key={index}
                                className={styles.tableRow}
                                onClick={
                                    (staff.studentStaffContactId && !staff.remove && !addMultiple) || staff.add
                                        ? () => removeAdvisor(index)
                                        : () => addAdvisor(index)
                                }>
                                <td>
                                    <div className={styles.fullnameWrap}>
                                        <img src={staff.photo} alt="" className={styles.avatar} />
                                        <p className={styles.fullname}>{getFullNameWithEnrollmentId(staff)}</p>
                                    </div>
                                </td>
                                <td className={styles.textAlignLeft}>{staff.title}</td>
                                <td>
                                    <div className={styles.defaultWrap}>{getIsDefault(staff, index)}</div>
                                </td>
                                <td className={styles.textAlignLeft}>{staff.roleName}</td>
                                <td>
                                    {(staff.studentStaffContactId && !staff.remove && !addMultiple) || staff.add ? (
                                        <div className={styles.checkIconWrap} onClick={() => removeAdvisor(index)}>
                                            <Icon icon="CHECKMARK_CIRCLE" />
                                        </div>
                                    ) : (
                                        <div className={styles.checkIconWrap} onClick={() => addAdvisor(index)}>
                                            <Icon icon="CHECK_CIRCLE" color="#E6E6E6" />
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {!staffs.length && (
                            <tr className={styles.tableRow}>
                                <td colSpan={5}>
                                    <p className={styles.emptyRowData}>{t("common:message.notResultFound")}</p>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        )
    }

    const renderStudentAdvisors = () => {
        if (inSearchMode) {
            return null
        }

        if (isLoadingStudentAdvisors) {
            return (
                <div className={styles.spinContainer}>
                    <Spin />
                </div>
            )
        }

        const obsoleteAdvisors = studentAdvisors.filter(
            (advisor) => !staffs.some((staff) => staff.staffProfileId === advisor.contactProfileId)
        )
        if (!obsoleteAdvisors.length) {
            return null
        }

        return (
            <div className={styles.tableWrapper}>
                <table className={styles.usersTable}>
                    <thead className={styles.titleColor}>
                        <tr>
                            <th>ASSIGNED ADVISORS (NO LONGER IN DEPARTMENT)</th>
                            <th className={styles.columnSize}>{t("advisorsPopup.title")}</th>
                            <th>{t("advisorsPopup.select")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {obsoleteAdvisors.map((staff, index) => (
                            <tr key={index} className={styles.tableRow} onClick={() => toggleObsoleteAdvisor(staff)}>
                                <td>
                                    <div className={styles.fullnameWrap}>
                                        <img src={staff.photo?.["64"]} alt="" className={styles.avatar} />
                                        <p className={styles.fullname}>{getFullNameWithEnrollmentId(staff)}</p>
                                    </div>
                                </td>
                                <td className={styles.textAlignLeft}>{staff.title}</td>
                                <td>
                                    {!state.removedObsoleteAdvisors.some(
                                        (advisor) => advisor.studentStaffContactId === staff.studentStaffContactId
                                    ) ? (
                                        <Icon icon="CHECKMARK_CIRCLE" />
                                    ) : (
                                        <Icon icon="CHECK_CIRCLE" color="#E6E6E6" />
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )
    }

    const hasPermissionToUpdateAdvisor = checkHasPermissionToUpdateAdvisor(departmentId, model) || model.isAdmin()

    return (
        <>
            <div className={styles.studentAdvisorsWrapper}>{renderStudentAdvisors()}</div>
            {renderAdvisorInfos()}
            {hasPermissionToUpdateAdvisor && (
                <div className={styles.saveBtnWrapper}>
                    <BaseButton
                        className={styles.saveButton}
                        title="SAVE"
                        onClick={updateStaffs}
                        loading={isSubmitting}
                    />
                </div>
            )}
        </>
    )
}
